import { memo, useEffect, useRef } from 'react';
import type { Theme } from '@mui/material';
import { alpha, styled } from '@mui/material';
import { isMobileApp } from 'utils/common';
import { useSelector, useDispatch } from 'react-redux';
import { selectDrawer } from 'store/entities/drawer/selectors';
import { AdminBreadcrumb } from './AdminBreadcrumb';
import { useLocation } from 'react-router-dom';
import { selectMenuOpened } from 'store/entities/dashboard/selectors';
import { AppBreadcrumbs } from './AppBreadcrumbs';
import { uiStateActions } from 'store/entities/ui';

const Wrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'mobileApp' && prop !== 'isDrawerOpen',
})(({ isDrawerOpen, open, mobileApp, theme }: { isDrawerOpen: boolean; open: boolean; mobileApp: boolean; theme?: Theme }) => ({
  position: 'fixed',
  zIndex: 20,
  width: '100%',
  backgroundColor: theme?.palette.background.default,
  top: '70px',
  paddingLeft: '24px',
  marginLeft: mobileApp ? 0 : open ? '320px' : '72px',
  paddingRight: isDrawerOpen ? '270px' : 0,
  transition: 'all 0.3s ease',
  color: alpha(theme?.palette.primary.main ?? '', 0.9),
  gap: '12px',
}));

export const Breadcrumbs = memo(({ hidden = false }: { hidden?: boolean }) => {
  const mobileApp = isMobileApp();
  const { pathname } = useLocation();
  const drawer = useSelector(selectDrawer());
  const dispatch = useDispatch();
  const open = useSelector(selectMenuOpened());
  const adminRoute = pathname.includes('/admin');
  const ref = useRef<HTMLDivElement>(null);

  /**
   * Check if the height of the breadcrumbs has changed and update the store
   * This is needed because the height of the breadcrumbs can change depending on the route
   */
  useEffect(() => {
    if (ref.current) {
      const height = ref.current.getBoundingClientRect().height;
      dispatch(uiStateActions.setComponentHeight({ id: 'breadcrumbs', height }));
    }

    return () => {
      dispatch(uiStateActions.removeComponentHeight('breadcrumbs'));
    };
  }, [dispatch, pathname]);

  if (hidden) return null;

  return (
    <Wrapper ref={ref} open={open} mobileApp={mobileApp} isDrawerOpen={drawer.isOpen} data-testid="app-breadcrumbs">
      {adminRoute ? <AdminBreadcrumb /> : !mobileApp && <AppBreadcrumbs />}
    </Wrapper>
  );
});
